import type {
    AuditRowComputed,
    AuditStepId,
    AuditStepState,
    AuditTableStatus,
    AuditTrailStep,
    NextGateTone,
} from './types';

const NEXT_GATE_BG: Record<NextGateTone, string> = {
    info: '!bg-status-info-surface',
    success: '!bg-status-success-surface',
};

const NEXT_GATE_TEXT: Record<NextGateTone, string> = {
    info: 'text-workflow-next-info',
    success: 'text-workflow-next-success',
};

const isNextLifecycleStepFor = (status: AuditTableStatus, step: AuditTrailStep): boolean =>
    (status === 'draft' && step.id === 'reviewed' && step.state === 'upcoming') ||
    (status === 'reviewed' && step.id === 'published' && step.state === 'upcoming');

const resolveRowBgClass = (isCurrent: boolean, isNextLifecycleStep: boolean, nextTone: NextGateTone): string => {
    if (isCurrent) {
        return '!bg-surface-raised';
    }
    if (isNextLifecycleStep) {
        return NEXT_GATE_BG[nextTone];
    }
    return '';
};

const resolveRowTextClass = (state: AuditStepState, isNextLifecycleStep: boolean, nextTone: NextGateTone): string => {
    if (state === 'current') {
        return 'text-text-primary';
    }
    if (state === 'done') {
        return 'text-text-content-subtle';
    }
    if (isNextLifecycleStep) {
        return NEXT_GATE_TEXT[nextTone];
    }
    return 'text-text-content-faint';
};

export const computeRowVisuals = (
    step: AuditTrailStep,
    status: AuditTableStatus,
    note: string | undefined,
    canEditNote: boolean,
    pendingLabel: string,
    formatTimestamp: (iso: string | null) => string,
    labelForStep: (stepId: AuditStepId) => string,
): AuditRowComputed => {
    const isCurrent = step.state === 'current';
    const isNextLifecycleStep = isNextLifecycleStepFor(status, step);
    const nextTone: NextGateTone = step.id === 'published' ? 'success' : 'info';

    const rowTextClass = resolveRowTextClass(step.state, isNextLifecycleStep, nextTone);
    const rowBgClass = resolveRowBgClass(isCurrent, isNextLifecycleStep, nextTone);

    const isMissingTimestamp = (step.id === 'reviewed' || step.id === 'published') && step.at === null;

    const isDraftedRow = step.id === 'draft' && step.state !== 'upcoming';
    const noteSlotVisible = isDraftedRow && (note !== undefined || canEditNote);

    return {
        isNextLifecycleStep,
        noteSlotVisible,
        rowBgClass,
        rowTextClass,
        showReviewerPill: step.id === 'reviewed' && step.state === 'upcoming',
        stampLabel: labelForStep(step.id),
        whenContent: isMissingTimestamp ? pendingLabel : formatTimestamp(step.at),
    };
};

export const shouldMuteActionBarStep = (step: AuditTrailStep): boolean => step.state === 'upcoming';

export const getActionBarStepTextClass = (step: AuditTrailStep, computed: AuditRowComputed): string =>
    shouldMuteActionBarStep(step) ? 'text-text-disabled' : computed.rowTextClass;

export const getActionBarSecondaryTextClass = (step: AuditTrailStep): string =>
    shouldMuteActionBarStep(step) ? 'text-text-disabled' : 'text-text-content-subtle';

export const getActionBarStepWeight = (step: AuditTrailStep): 'medium' | 'semibold' =>
    shouldMuteActionBarStep(step) ? 'medium' : 'semibold';
